import { useEffect, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { useCatalog } from '../../contexts/CatalogContext';
import { rating } from '../../core/format/currency';
import { homeAnchor } from '../../data/homeSections';
import { HomeHero } from './HomeHero';
import { HomeManifest } from './HomeManifest';
import { ConcernFinder } from './ConcernFinder';
import { HomeShelf } from './HomeShelf';
import { HomeStory } from './HomeStory';
import { HomeReviews } from './HomeReviews';
import { HomeProtocols } from './HomeProtocols';
import { HomeJournal } from './HomeJournal';
import { HomeVisit } from './HomeVisit';
import { HOME_SHELF, homeBestsellers } from './homeRules';
import type { HomePage } from '../../core/types/pages';

/**
 * De homepage als één verhaal (docs/VORMGEVING.md §3, volgorde in data/homeSections.ts):
 *
 * 1. opening (sand): wie, wat, en het huidadvies als startpunt
 * 2. waar ik voor sta (blush): de balans salon/thuis
 * 3. zo werk ik (salie): Kijken, Kiezen, Opbouwen; knop naar een afspraak
 * 4. kiezen (shell): per klacht het protocol en de behandeling, opzij te swipen
 * 5. opbouwen (sand): wat er na je bestelling gebeurt
 * 6. reviews (shell): bewijs, de brug naar klein beginnen
 * 7. één product (sand): de plank
 * 8. journal (klei): veelgestelde vragen, met producten en de app-kaart
 * 9. kom langs (blush), daarna de footer (klei)
 *
 * Elk blok heeft een ankertje (`/#zo-werk-ik`), zodat het beheer naar precies dat blok kan linken.
 */
export function Home() {
  const {
    products,
    shopRating
  } = useCatalog();
  const {
    hash
  } = useLocation();
  const shelf = homeBestsellers(products, HOME_SHELF);

  // De homepage laadt los; een ankertje uit de link pakken we op zodra de blokken er staan.
  useEffect(() => {
    if (hash.length < 2) return;
    document.getElementById(decodeURIComponent(hash.slice(1)))?.scrollIntoView({
      block: 'start'
    });
  }, [hash]);
  return <>
      <Block id="hero" data-id="element-7349" data-mp="18p57x8kyh9xe">
        <HomeHero reviewCount={shopRating.count} avgRating={rating(shopRating.average)} data-id="element-7350" data-mp="18rzrkxw5aqpr" />
      </Block>
      <Block id="manifest" data-id="element-7351" data-mp="5h3rgovp1glv">
        <HomeManifest data-id="element-7352" data-mp="adu9hhr26m9x" />
      </Block>
      <Block id="story" data-id="element-7353" data-mp="acsvb4eqxjnz">
        <HomeStory data-id="element-7354" data-mp="ag6z8h3260xj" />
      </Block>
      <Block id="concerns" data-id="element-7355" data-mp="fdglxoz2byho">
        <ConcernFinder data-id="element-7356" data-mp="fe8jh5c806al" />
      </Block>
      <Block id="protocols" data-id="element-7357" data-mp="fb2q3dwk6i92">
        <HomeProtocols data-id="element-7358" data-mp="jeil65szcajn" />
      </Block>
      <Block id="reviews" data-id="element-7359" data-mp="jdhc26vixy4k">
        <HomeReviews data-id="element-7360" data-mp="jcob5mj8y2uc" />
      </Block>
      {shelf.length > 0 && <Block id="bestsellers" data-id="element-7361" data-mp="3iv753jvb82r6">
          <HomeShelf products={shelf} total={products.length} data-id="element-7362" data-mp="3irt4ag2ao1lu" />
        </Block>}
      <Block id="journal" data-id="element-7363" data-mp="3nt8oy70k863p">
        <HomeJournal data-id="element-7364" data-mp="3rulu0evtw59k" />
      </Block>
      <Block id="visit" data-id="element-7365" data-mp="3ruc5ywucg86i">
        <HomeVisit data-id="element-7366" data-mp="3rt126x0wf0fe" />
      </Block>
    </>;
}

/** Ankertje rond een blok; `scroll-mt` houdt het vrij van de header. */
function Block({
  id,
  children
}: {
  id: keyof HomePage;
  children: ReactNode;
}) {
  return <div id={homeAnchor(id)} className="scroll-mt-24" data-id="element-7367" data-mp="30361k47lbobf">
      {children}
    </div>;
}